import { staticChargingData } from '../../config/staticData/charging-data.static';
import { Module } from 'vuex';
import { debounceTime, Subject } from 'rxjs';
import { UsaFetchResult } from './charging-data-usa.interface';
import type { Store } from '../store';
import {
  BatteryId,
  SimulationData,
  BatteryMap,
  BaumusterBatteryMapping,
  Battery,
  ChargingOption,
  Model,
  Vehicle,
  ApiInformation,
  Mileage,
  BatteryChangingInformation,
  ApiFetchResult,
  EuropeApiFetchResult,
  BatteryChangingInformationSeparated,
  PowerConsumptionUnit,
} from './charging-data.interface';
import { isNafta } from '@/config/translation/available-locales';

export interface State {
  chargingOptions: ChargingOption[];
  batteries: BatteryMap;
  models: Model[];
  selectedModel: Model; //mutation
  selectedVehicle: Vehicle; //mutation
  chargingValue: number;
  chargingValueStart: number;
  preSelectedModelSeries: string;
  preSelectedVehicleIds: string[];
  baumusterBatteryMapping: BaumusterBatteryMapping;
  apiInformation: ApiInformation;
}

interface DownloadPipelineInput {
  locale: string;
  fetchInformation: ApiInformation;
}

const downloadPipeline = new Subject<DownloadPipelineInput>();
const getResultFromObservalbe = (): Promise<ApiFetchResult> => {
  return new Promise((resolve) => {
    downloadPipeline.pipe(debounceTime(50)).subscribe(async (downloadPipelineInput) => {
      const fetchResult = await fetchData(downloadPipelineInput);
      resolve(fetchResult);
    });
  });
};
const EUROPE_BFF = 'https://eqready-app.mercedes-benz.com/predict-api/v2/vehicledata';
const EUROPE_KEY = '55eb9e0b-7e24-470f-854e-f810c559cba8';
const initialState = (): State => {
  return {
    ...staticChargingData,
    chargingValue: 80,
    chargingValueStart: 0,
    preSelectedModelSeries: '',
    preSelectedVehicleIds: [],
    chargingOptions: [],
    selectedModel: {} as Model,
    selectedVehicle: {} as Vehicle,
    apiInformation: {
      key: EUROPE_KEY,
      url: EUROPE_BFF,
    },
  };
};

export const chargingData: Module<State, Store> = {
  namespaced: true,
  state: initialState(),
  getters: {
    loading: (state) => {
      return state.models.length === 0;
    },
    battery:
      (state) =>
      (id: string): Battery => {
        return state.batteries[id];
      },
    models: (state) => {
      return filterByPreSelectedModelSeries(state.models, state.preSelectedModelSeries);
    },
    hasModels: (state) => {
      return filterByPreSelectedModelSeries(state.models, state.preSelectedModelSeries).length > 0;
    },
    selectedModel: (state) => {
      return state.selectedModel;
    },
    selectedVehicle: (state) => {
      return state.selectedVehicle;
    },
    hasChargingOptions: (_, getters): boolean => {
      return getters.batteryChangingInformation.length > 0;
    },
    hasPublicChargingOptions: (_, getters): boolean => {
      return getters.batteryChangingInformationSeparated.public.length > 0;
    },
    hasPrivateChargingOptions: (_, getters): boolean => {
      return getters.batteryChangingInformationSeparated.home.length > 0;
    },
    vehicles: (state): Vehicle[] => {
      const model = state.models.find((model) => {
        return state.selectedModel.modelSeries === model.modelSeries;
      });
      if (model) {
        return model.vehicles;
      }
      return [];
    },
    batteryChangingInformation: (state): BatteryChangingInformation[] => {
      if (state.selectedVehicle.typeId) {
        const batteryId = getBatteryIdByVehicle(state.baumusterBatteryMapping, state.selectedVehicle.typeId);
        const battery = state.batteries[batteryId];
        if (battery) {
          return state.chargingOptions.map((chargingOption) => {
            const simulationData = findBatteryMapped2ChargingOptions(battery, chargingOption);
            if (simulationData) {
              let loadingDuration: number = Math.round(
                (simulationData[state.chargingValue - 1] || 0) - (simulationData[state.chargingValueStart - 1] || 0),
              );
              if (state.chargingValueStart !== state.chargingValue) {
                if (loadingDuration <= 0) {
                  loadingDuration = 24 * 60 + 1; // if difference = 0 for different start and finish values then > 24h
                }
              }
              return {
                ...chargingOption,
                loadingDurationMinutes: loadingDuration,
              };
            }
            // return no duration
            return { ...chargingOption, loadingDurationMinutes: 0 };
          });
        }
      }
      return [];
    },
    batteryChangingInformationSeparated: (state, getters): BatteryChangingInformationSeparated => {
      const initial: BatteryChangingInformationSeparated = {
        public: [],
        home: [],
      };

      const batteryChargings = getters.batteryChangingInformation as BatteryChangingInformation[];

      const batteryChangingInformationsSeparated = batteryChargings.reduce<BatteryChangingInformationSeparated>(
        (previous, current) => {
          current.isPublic ? previous.public.push(current) : previous.home.push(current);
          return previous;
        },
        initial,
      );

      return batteryChangingInformationsSeparated;
    },
    mileage: (state): Mileage | undefined => {
      if (!state.selectedVehicle?.range) {
        return undefined;
      }
      const { min: lowConsumptionRange, max: highConsumptionRange } = state.selectedVehicle.range;
      const minMileage = ((state.chargingValue - state.chargingValueStart) / 100) * highConsumptionRange;
      const maxMileage = ((state.chargingValue - state.chargingValueStart) / 100) * lowConsumptionRange;

      return {
        min: minMileage,
        max: maxMileage,
        unit: state.selectedVehicle.range.unit,
      };
    },
  },
  actions: {
    setChargingOptions: ({ commit }, chargingOptions: ChargingOption[]) => {
      commit('setChargingOptions', chargingOptions);
    },
    setPreSelectedVehicleIds: ({ commit }, vehicleIds: string[]) => {
      commit('setPreSelectedVehicleIds', vehicleIds);
    },
    setModelSeries: async ({ commit, state, dispatch }, modelSeries: string) => {
      const selectedModels = filterByPreSelectedModelSeries(state.models, modelSeries);
      if (selectedModels.length === 1) {
        await dispatch('setSelectedModel', selectedModels[0]);
      }
      commit('setModelSeries', modelSeries);
    },
    setSelectedModel: ({ commit }, model: Model) => {
      commit('setSelectedModel', model);
    },
    setSelectedVehicle: ({ commit }, selectedVehicle: Vehicle) => {
      commit('setSelectedVehicle', selectedVehicle);
    },
    setBatteryCharging: ({ commit }, chargingValue: number) => {
      commit('setBatteryCharging', chargingValue);
    },
    setBatteryChargingStart: ({ commit }, chargingValueStart: number) => {
      commit('setBatteryChargingStart', chargingValueStart);
    },
    /**
     * Used in case of predefined vehicle is available from outer context (e.g. seamless store)
     * @param vehicle the predefined vehicle
     */
    setModelsByPredefinedVehicle: ({ state, commit }, vehicle: Vehicle) => {
      const selectedModels: Model[] = [
        {
          bodytypeId: 'undefined', // this information is not included in the predefined vehicle, but also not needed here
          classId: vehicle.classId,
          className: vehicle.className,
          image: vehicle.image,
          modelSeries: vehicle.modelSeries,
          vehicles: [vehicle],
        },
      ];
      const modelsWithValidBatteryIds = filterByValidBatteryId(state, selectedModels);
      commit('initModels', modelsWithValidBatteryIds);
    },
    /**
     * Used in default case. Loads all models, filters by predefined modelseries and filters by available battery ids from static mapping
     */
    loadModels: async ({ commit, state }, locale: string) => {
      getResultFromObservalbe().then((data: ApiFetchResult) => {
        const selectedModels = filterByPreSelectedModelSeries(data.models, state.preSelectedModelSeries);

        if (selectedModels.length === 1) {
          commit('setSelectedModel', selectedModels[0]);
        }

        const modelsWithValidBatteryIds = filterByValidBatteryId(state, selectedModels);
        commit('initModels', modelsWithValidBatteryIds);
      });

      // trigger the actual download debounced due to locale changes (only load last set locale, not all)
      downloadPipeline.next({
        locale,
        fetchInformation: state.apiInformation,
      });
    },
    setApiInformation: ({ commit }, apiInformation: ApiInformation) => {
      commit('setApiInformation', apiInformation);
    },
  },
  mutations: {
    initModels: (state, models: Model[]) => {
      if (models.length === 0) {
        return;
      }
      state.models = models;
      state.selectedModel = models[0];
      state.selectedVehicle = models[0].vehicles[0];
    },
    setApiInformation: (state, apiInformation: ApiInformation) => {
      state.apiInformation = apiInformation;
    },
    setChargingOptions: (state, chargingOptions: ChargingOption[]) => {
      state.chargingOptions = chargingOptions;
    },
    setPreSelectedVehicleIds: (state, vehicleIds: string[]) => {
      state.preSelectedVehicleIds = vehicleIds;
    },
    setModelSeries: (state, modelSeries: string) => {
      state.preSelectedModelSeries = modelSeries;
    },
    setSelectedModel: (state, model: Model) => {
      state.selectedModel = model;
      state.selectedVehicle = model.vehicles[0];
    },
    setSelectedVehicle: (state, selectedVehicle: Vehicle) => {
      state.selectedVehicle = selectedVehicle;
    },
    setBatteryCharging: (state, chargingValue: number) => {
      state.chargingValue = chargingValue;
    },
    setBatteryChargingStart: (state, chargingValueStart: number) => {
      state.chargingValueStart = chargingValueStart;
    },
  },
};

const filterByPreSelectedVehicle = (models: Model[], preSelectedVehicleIds: string[]): Model[] => {
  if (preSelectedVehicleIds.length === 0) {
    return models;
  }
  return models.reduce<Model[]>((acc, model) => {
    const filterdVehicles = model.vehicles.filter((vehicle) => preSelectedVehicleIds.includes(vehicle.name));
    if (filterdVehicles.length > 0) {
      const temp: Model = { ...model, vehicles: filterdVehicles };
      acc.push(temp);
    }
    return acc;
  }, []);
};

const filterByPreSelectedModelSeries = (models: Model[], modelSeriesId: string): Model[] => {
  if (modelSeriesId === '' || !modelSeriesId) {
    return models;
  }

  return models.filter((model) => {
    return model.modelSeries.toUpperCase() === modelSeriesId.toUpperCase();
  });
};

export const findBatteryMapped2ChargingOptions = (b: Battery, c: ChargingOption): SimulationData | undefined => {
  return b[c.power];
};

const getBatteryIdByVehicle = (mapping: BaumusterBatteryMapping, typeId: string): BatteryId => {
  const baumusterId = typeId.split('-')[0].substring(0, 6); //Use baumusterId in future
  return mapping[baumusterId];
};

const filterByValidBatteryId = (state: State, models: Model[]): Model[] => {
  return models.reduce<Model[]>((result, model) => {
    const possibleVehicles = model.vehicles.filter((vehicle) => {
      const batteryId = getBatteryIdByVehicle(state.baumusterBatteryMapping, vehicle.typeId);
      return state.batteries[batteryId] !== undefined;
    });
    if (possibleVehicles.length > 0) {
      model.vehicles = possibleVehicles;
      result.push(model);
    }
    return result;
  }, []);
};

export const fetchData = async ({ locale, fetchInformation }: DownloadPipelineInput): Promise<ApiFetchResult> => {
  let headers = {};
  if (fetchInformation.key) {
    headers = {
      'x-api-key': fetchInformation.key,
    };
  }
  const fetchUrl = new URL(fetchInformation.url);
  fetchUrl.searchParams.append('locale', locale);
  const res = await fetch(fetchUrl.toString(), { headers });

  let data: ApiFetchResult;
  if (isNafta(locale)) {
    const usadata: UsaFetchResult = await res.json();
    data = mapUsaFetchResultToFetchResult(usadata);
  } else {
    const result = (await res.json()) as EuropeApiFetchResult;
    data = result.bodytypes.reduce<ApiFetchResult>(
      (obj, bodyType) => {
        obj.models = [...obj.models, ...bodyType.models];
        return obj;
      },
      { models: [] },
    );
    // Needed as long the request not return an image at model level
    data.models = data.models.map((model) => {
      const img = model.vehicles.length > 0 ? model.vehicles[0].image : '';
      return { ...model, image: img };
    });
  }

  return data;
};

const mapUsaFetchResultToFetchResult = (usaFetchResult: UsaFetchResult): ApiFetchResult => {
  return {
    models: usaFetchResult.models.map<Model>((usaModel) => {
      return {
        bodytypeId: 'not impl',
        classId: usaModel.className.toLocaleLowerCase() + '-class',
        className: usaModel.className,
        image: usaModel.vehicles.find((vehicle) => vehicle.baumuster === usaModel.defaultModel)?.image ?? '',
        modelSeries: usaModel.className, //  We have no modelSeries at usa so we use ClassName
        vehicles: usaModel.vehicles.map<Vehicle>((usaVehicle) => {
          return {
            co2Emission: 'not impl',
            classId: 'not impl',
            className: 'not impl',
            modelSeries: 'not impl',
            lifeCycle: 'FOR_SALE',
            electricPowerConsumption: { individual: 0, max: 0, min: 0, unit: 'kWh/100km' as PowerConsumptionUnit }, // proper units need to be added
            electricPowerConsumptionTotal: 0,
            engineConcept: 'ELECTRIC',
            financeData: { currency: 'EUR', netPrice: 0, price: 0 },
            fuelConsumptionTotal: 0,
            fuelConsumptionWeighted: { individual: 0, max: 0, min: 0, unit: 'l/100km' as PowerConsumptionUnit }, // proper units need to be added
            fuelType: ['ELECTRIC'],
            image: usaVehicle.image,
            images: { small: usaVehicle.image, medium: '', large: '', altText: '' },
            baumuster: usaVehicle.baumuster,
            installedAcChargingPower: usaVehicle.installedAcChargingPower,
            installedDcChargingPower: usaVehicle.installedDcChargingPower,
            name: usaVehicle.name,
            typeId: usaVehicle.baumuster, // miss matching...
            underlyingTestCycle: 'WLTP',
            range: {
              individual: usaVehicle.range.value,
              min: usaVehicle.range.value, //advised no explizit data there
              max: usaVehicle.range.value, //advised no explizit data there
              unit: usaVehicle.range.unit as PowerConsumptionUnit,
            },
          };
        }),
      };
    }),
  };
};
